import { useRef } from "react";
import { Bell, Settings2, Smartphone } from "lucide-react";
import { statusBarSwipeTarget, type StatusBarTouch } from "../../core/status-bar-gesture";
import { routeDestinationLabel, routeHomeLabel } from "../../core/route-summary";
import type { CommuteSnapshot } from "../../shared/types";
import { BUILD_TAG } from "../build";
import { clock, duration, relativeAge, stationLabel } from "../../core/format";

/**
 * Simulated data must never be mistaken for the real thing, so this sits above everything and
 * spells out exactly which inputs are fake.
 *
 * The clock is not one of them: nothing here simulates time, and printing it only invited the
 * question of whether it was real.
 */
export function SimulationBanner({
	snapshot,
	locationLabel,
	onExit,
}: {
	snapshot: CommuteSnapshot;
	/** Where he is pretending to stand, which the worker never hears about. */
	locationLabel: string | null;
	onExit: () => void;
}) {
	if (!snapshot.simulated && locationLabel == null) {
		return null;
	}

	const details: string[] = [];
	if (locationLabel != null) {
		details.push(`location: ${locationLabel}`);
	}
	if (snapshot.drive?.note === "Simulated drive time.") {
		details.push(`drive ${Math.round(snapshot.drive.durationSeconds / 60)} min`);
	}
	if (!snapshot.feed.available) {
		details.push("realtime off");
	}

	return (
		<section className="simbanner">
			<span className="simbanner__tag">Simulated</span>
			<span className="simbanner__detail">{details.join(" · ")}</span>
			<button type="button" onClick={onExit}>
				Exit
			</button>
		</section>
	);
}

interface RoutePickerProps {
	snapshot: CommuteSnapshot;
	direction: "school" | "home";
	onDirectionChange: (direction: "school" | "home") => void;
	onOpenAlerts: () => void;
	pickupOrigin?: string;
	hideDirection?: boolean;
}

/**
 * The route summary, and the way into the sheet that changes it.
 *
 * There is no picker of its own: which commute to watch, when to arrive and where the drive
 * starts all live in the alerts sheet, so tapping the summary opens that rather than offering
 * a second, half-featured chooser.
 */
export function RoutePicker({ snapshot, direction, onDirectionChange, onOpenAlerts, pickupOrigin, hideDirection = false }: RoutePickerProps) {
	const { profile, targetWindow, declaredTargetMs } = snapshot;
	const dormantTarget = targetWindow == null && declaredTargetMs != null;
	const destination = routeDestinationLabel(profile, snapshot.route.toStationId);

	return (
		<section className="picker">
			<button type="button" className="picker__summary" onClick={onOpenAlerts}>
				<span className="picker__route">
					{pickupOrigin ?? stationLabel(profile.boardingStation.name)}
					<span className="picker__arrow" aria-label="to">
						→
					</span>
					{destination}
					{!pickupOrigin && (targetWindow || dormantTarget) ? (
						<span className={`picker__target ${dormantTarget ? "is-dormant" : ""}`}>
							{clock(targetWindow?.targetMs ?? declaredTargetMs, snapshot.timezone)}
						</span>
					) : !pickupOrigin && direction === "home" ? (
						<span className="picker__target is-dormant">{routeHomeLabel(snapshot.route.toStationId)}</span>
					) : null}
				</span>
			</button>

			{/* The only thing left to choose here: everything else lives in the alerts sheet. */}
			{!hideDirection ? <div className="picker__direction" role="group" aria-label="Direction">
				<button
					type="button"
					className={direction === "school" ? "is-active" : ""}
					aria-pressed={direction === "school"}
					onClick={() => onDirectionChange("school")}
				>
					To School
				</button>
				<button
					type="button"
					className={direction === "home" ? "is-active" : ""}
					aria-pressed={direction === "home"}
					onClick={() => onDirectionChange("home")}
				>
					Return Home
				</button>
			</div> : null}
		</section>
	);
}

interface StatusBarProps {
	snapshot: CommuteSnapshot | null;
	fetchedAtMs: number | null;
	nowMs: number;
	loading: boolean;
	offline: boolean;
	alertsEnabled: boolean;
	/** Whether the iOS shell is hosting us, and whether it owes a rebuild. */
	native: { active: boolean; stale: boolean };
	onRefresh: () => void;
	onOpenSettings: () => void;
	onOpenAlerts: () => void;
}

export function StatusBar({ snapshot, fetchedAtMs, nowMs, loading, offline, alertsEnabled, native, onRefresh, onOpenSettings, onOpenAlerts }: StatusBarProps) {
	const feed = snapshot?.feed;
	const age = feed?.ageSeconds == null ? "" : ` ${duration(feed.ageSeconds)}`;

	let feedLabel: string;
	let tone: string;
	if (!snapshot) {
		feedLabel = "connecting";
		tone = "";
	} else if (offline) {
		feedLabel = "offline — last known";
		tone = "is-bad";
	} else if (snapshot.simulated) {
		feedLabel = "simulated data";
		tone = "is-warn";
	} else if (!feed?.available) {
		feedLabel = "BART scheduled only";
		tone = "is-warn";
	} else if (feed.stale) {
		feedLabel = `BART stale${age}`;
		tone = "is-warn";
	} else {
		feedLabel = `BART live${age}`;
		tone = "is-good";
	}

	// Both sheets live at the outer edges with their buttons. The middle stays inert so the most
	// common upward gesture cannot open either one by accident.
	const swipeRef = useRef<StatusBarTouch | null>(null);

	// Opening on the move rather than on release: waiting for the finger to lift made the sheet
	// feel like a consequence of the gesture instead of the thing being dragged into view.
	const open = (target: "settings" | "alerts") => {
		swipeRef.current = null;
		if (target === "settings") {
			onOpenSettings();
		} else {
			onOpenAlerts();
		}
	};

	return (
		<footer
			className={`statusbar ${offline ? "is-offline" : ""}`}
			onTouchStart={(event) => {
				const touch = event.touches[0];
				swipeRef.current = { x: touch.clientX, y: touch.clientY, width: event.currentTarget.getBoundingClientRect().width };
			}}
			onTouchMove={(event) => {
				const start = swipeRef.current;
				const target = start == null ? null : statusBarSwipeTarget(start, event.touches[0].clientY);
				if (target != null) {
					open(target);
				}
			}}
			onTouchEnd={(event) => {
				const start = swipeRef.current;
				swipeRef.current = null;
				const target = start == null ? null : statusBarSwipeTarget(start, event.changedTouches[0].clientY);
				if (target != null) {
					open(target);
				}
			}}
			onTouchCancel={() => {
				swipeRef.current = null;
			}}
		>
			{/* Settings and alerts sit at opposite corners so neither is hit by mistake. */}
			<button type="button" className="statusbar__icon" onClick={onOpenSettings} aria-label="Settings">
				<Settings2 size={22} strokeWidth={2} aria-hidden="true" />
			</button>
			{/* Which of the two GoTimes this is — the shell and Safari look identical otherwise. */}
			{native.active ? (
				<span
					className={`statusbar__native ${native.stale ? "is-stale" : ""}`}
					role="img"
					aria-label={native.stale ? "iOS app, out of date" : "iOS app"}
					title={native.stale ? "iOS app — older than this page, so it owes a rebuild" : "Running in the iOS app"}
				>
					<Smartphone size={15} strokeWidth={2} aria-hidden="true" />
				</span>
			) : null}
			<button type="button" className="statusbar__refresh" onClick={onRefresh} disabled={loading}>
				<span className={`dot ${offline ? "is-bad" : loading ? "is-busy" : tone}`} />
				{feedLabel}
				<span className="statusbar__age">updated {relativeAge(fetchedAtMs, nowMs)}</span>
				{/* Whether a deploy actually reached this phone is otherwise unanswerable from the couch. */}
				<span className="statusbar__build">{BUILD_TAG}</span>
			</button>
			<button
				type="button"
				className={`statusbar__icon statusbar__alerts ${alertsEnabled ? "is-enabled" : ""}`}
				onClick={onOpenAlerts}
				aria-label={alertsEnabled ? "Alerts enabled" : "Alerts disabled"}
				aria-pressed={alertsEnabled}
			>
				<Bell size={22} strokeWidth={2} fill={alertsEnabled ? "currentColor" : "none"} aria-hidden="true" />
			</button>
		</footer>
	);
}
